import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

const MotionBox = motion(Box);

// URL Base de WhatsApp (dejamos la variable lista para concatenar el mensaje)
const whatsappBaseUrl =
  "https://api.whatsapp.com/send/?phone=593985092679&type=phone_number&app_absent=0";

const plans = [
  {
    name: "Plan Básico",
    tag: null,
    description:
      "Una solución profesional para negocios que necesitan facturar electrónicamente y controlar su operación diaria desde internet.",
    features: [
      { label: "50 documentos electrónicos", included: true },
      { label: "Control de inventarios", included: true },
      { label: "Hasta 5 usuarios", included: true },
      { label: "Proformas", included: true },
      { label: "Sistema en subdominio", included: true },
      { label: "Acceso desde cualquier dispositivo", included: true },
      { label: "Manejo de stock", included: true },
      { label: "Clientes y proveedores", included: true },
      { label: "Cuentas y pagos", included: true },
      { label: "Retenciones", included: true },
    ],
    cta: "Solicitar información",
    whatsappMsg: "Hola, deseo más información sobre el Plan Básico.",
    featured: false,
    accent: "#E8F0FF",
    accentBorder: "rgba(44,95,190,0.12)",
  },
  {
    name: "Plan Profesional",
    tag: "Recomendado",
    description:
      "Pensado para empresas que necesitan operar sin límites y con todas las herramientas administrativas incluidas.",
    features: [
      { label: "Usuarios ilimitados", included: true },
      { label: "Documentos electrónicos ilimitados", included: true },
      { label: "Control de inventarios", included: true },
      { label: "Proformas", included: true },
      { label: "Sistema en subdominio", included: true },
      { label: "Acceso desde cualquier dispositivo", included: true },
      { label: "Manejo de stock", included: true },
      { label: "Clientes y proveedores", included: true },
      { label: "Cuentas y pagos", included: true },
      { label: "Retenciones", included: true },
    ],
    cta: "Contactar por WhatsApp",
    whatsappMsg: "Hola, me interesa adquirir el Plan Profesional.",
    featured: true,
    accent: "#2C5FBE",
    accentBorder: "#2C5FBE",
  },
  {
    name: "Plan Catálogo Online",
    tag: null,
    description:
      "Incluye todo lo del plan profesional y añade la vitrina digital para mostrar productos en línea de forma moderna y accesible.",
    features: [
      { label: "Todo lo del Plan Profesional", included: true },
      { label: "Catálogo de productos en línea", included: true },
      { label: "Visualización de productos para clientes", included: true },
      { label: "Botón de compra por WhatsApp", included: true },
      { label: "Acceso desde cualquier dispositivo", included: true },
      { label: "Sistema en internet bajo subdominio", included: true },
      { label: "Mejor experiencia comercial", included: true },
      { label: "Mayor visibilidad para sus productos", included: true },
    ],
    cta: "Quiero este plan",
    whatsappMsg: "Hola, quiero adquirir el Plan Catálogo Online.",
    featured: false,
    accent: "#E8F0FF",
    accentBorder: "rgba(44,95,190,0.12)",
  },
];

function CheckIcon({ included, planFeatured }) {
  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: "5px",
        bgcolor: included
          ? planFeatured
            ? "rgba(255,255,255,0.2)"
            : "rgba(44,95,190,0.12)"
          : "rgba(148,163,184,0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {included ? (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path
            d="M2 5l2.5 2.5L8 3"
            stroke={planFeatured ? "white" : "#2C5FBE"}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
          <path
            d="M2 2l4 4M6 2L2 6"
            stroke="#CBD5E1"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      )}
    </Box>
  );
}

function PlanCard({ plan, index }) {
  return (
    <MotionBox
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: index * 0.1, duration: 0.5, ease: "easeOut" }}
      whileHover={
        !plan.featured ? { y: -4, transition: { duration: 0.2 } } : {}
      }
      sx={{
        position: "relative",
        flex: 1,
        borderRadius: "20px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",

        ...(plan.featured
          ? {
              bgcolor: "#2C5FBE",
              boxShadow: "0 20px 40px rgba(44,95,190,0.25)",
              transform: { md: "scale(1.03)" },
              zIndex: 2,
            }
          : {
              bgcolor: "#fff",
              border: `1px solid ${plan.accentBorder}`,
              boxShadow: "0 4px 20px rgba(15,23,42,0.04)",
            }),
      }}
    >
      {/* Borde superior featured */}
      {!plan.featured && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: "linear-gradient(90deg, #2C5FBE 0%, #4F8CFF 100%)",
          }}
        />
      )}

      {/* Geometría para el plan destacado */}
      {plan.featured && (
        <>
          <Box
            sx={{
              position: "absolute",
              top: -30,
              right: -30,
              width: 120,
              height: 120,
              opacity: 0.08,
              pointerEvents: "none",
            }}
          >
            <svg viewBox="0 0 160 160" fill="none">
              <polygon
                points="80,5 155,42 155,118 80,155 5,118 5,42"
                fill="white"
              />
            </svg>
          </Box>
        </>
      )}

      <Box
        sx={{
          p: { xs: 2.5, md: 3 },
          display: "flex",
          flexDirection: "column",
          flex: 1,
          gap: 2,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Etiqueta / Badge */}
        <Box sx={{ display: "flex", minHeight: "24px" }}>
          {plan.tag && (
            <Box
              sx={{
                px: 1.2,
                py: 0.4,
                bgcolor: "rgba(255,255,255,0.2)",
                borderRadius: "6px",
                backdropFilter: "blur(4px)",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: "white",
                  letterSpacing: "0.05em",
                }}
              >
                ★ {plan.tag}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Encabezado del Plan */}
        <Box>
          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: plan.featured ? "rgba(255,255,255,0.7)" : "#2C5FBE",
              mb: 0.3,
            }}
          >
            PLAN
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.4rem",
              color: plan.featured ? "#fff" : "#1A2B4A",
              lineHeight: 1.1,
            }}
          >
            {plan.name}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontSize: "0.8rem",
              color: plan.featured ? "rgba(255,255,255,0.8)" : "#64748B",
              lineHeight: 1.4,
              minHeight: { xs: "auto", md: "50px" }, // Asegura alineación en desktop
            }}
          >
            {plan.description}
          </Typography>
        </Box>

        {/* Divisor */}
        <Box
          sx={{
            height: "1px",
            bgcolor: plan.featured
              ? "rgba(255,255,255,0.12)"
              : "rgba(44,95,190,0.08)",
            my: 0.5,
          }}
        />

        {/* Características */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.2,
            flex: 1,
            mb: 2,
          }}
        >
          {plan.features.map((feat, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckIcon
                included={feat.included}
                planFeatured={plan.featured}
              />
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: feat.included
                    ? plan.featured
                      ? "rgba(255,255,255,0.95)"
                      : "#334155"
                    : plan.featured
                      ? "rgba(255,255,255,0.4)"
                      : "#CBD5E1",
                  fontWeight: feat.included ? 500 : 400,
                }}
              >
                {feat.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Botón CTA apuntando a WhatsApp */}
        <Button
          component="a"
          href={`${whatsappBaseUrl}&text=${encodeURIComponent(plan.whatsappMsg)}`}
          target="_blank"
          rel="noopener noreferrer"
          fullWidth
          variant={plan.featured ? "contained" : "outlined"}
          sx={{
            borderRadius: "10px",
            py: 1,
            fontWeight: 750,
            textTransform: "none",
            fontSize: "0.85rem",
            mt: "auto", // Mantiene los botones alineados abajo
            ...(plan.featured
              ? {
                  bgcolor: "#fff",
                  color: "#2C5FBE",
                  "&:hover": {
                    bgcolor: "#F0F5FF",
                  },
                }
              : {
                  borderColor: "#2C5FBE",
                  color: "#2C5FBE",
                  "&:hover": {
                    bgcolor: "rgba(44,95,190,0.04)",
                  },
                }),
          }}
        >
          {plan.cta}
        </Button>
      </Box>
    </MotionBox>
  );
}

// Generador de partículas para el fondo
const particles = Array.from({ length: 12 }).map((_, i) => ({
  id: i,
  size: Math.random() * 8 + 4,
  left: `${Math.random() * 100}%`,
  top: `${Math.random() * 100}%`,
  duration: Math.random() * 15 + 10,
}));

export default function PricingSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeIndex, setActiveIndex] = useState(0);

  // Auto-play en móvil (cada 5 segundos)
  useEffect(() => {
    if (!isMobile) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev >= plans.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [isMobile]);

  // Manejo de swipe
  const handleDragEnd = (e, { offset }) => {
    const swipe = offset.x;
    if (swipe < -40) {
      setActiveIndex((prev) => (prev >= plans.length - 1 ? 0 : prev + 1));
    } else if (swipe > 40) {
      setActiveIndex((prev) => (prev === 0 ? plans.length - 1 : prev - 1));
    }
  };
  return (
    <Box
      id="planes"
      component="section"
      sx={{
        py: { xs: 6, md: 8 },
        position: "relative",
        overflow: "hidden",
        bgcolor: "#F8FAFF",
      }}
    >
      {/* Sistema de Partículas Flotantes */}
      {particles.map((particle) => (
        <MotionBox
          key={particle.id}
          animate={{
            y: [0, -40, 0],
            x: [0, Math.random() * 20 - 10, 0],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          sx={{
            position: "absolute",
            left: particle.left,
            top: particle.top,
            width: particle.size,
            height: particle.size,
            borderRadius: "50%",
            backgroundColor: "#2C5FBE",
            zIndex: 0,
          }}
        />
      ))}

      {/* Hexágono gigante sutil de fondo */}
      <Box
        sx={{
          position: "absolute",
          left: -100,
          top: "20%",
          width: 350,
          height: 350,
          opacity: 0.02,
          pointerEvents: "none",
        }}
      >
        <svg viewBox="0 0 400 400" fill="none">
          <polygon
            points="200,10 390,110 390,290 200,390 10,290 10,110"
            fill="#2C5FBE"
          />
        </svg>
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Encabezado */}
        <MotionBox
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          sx={{ mb: { xs: 4, md: 5 }, textAlign: "center" }}
        >
          <Typography
            component="h2"
            sx={{
              fontWeight: 750,
              fontSize: { xs: "2rem", md: "2.6rem" },
              color: "#1A2B4A",
              lineHeight: 1.1,
              mb: 1,
            }}
          >
            Planes diseñados para{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(135deg, #2C5FBE 0%, #4F8CFF 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              tu crecimiento
            </Box>
          </Typography>

          <Typography
            sx={{
              color: "#64748B",
              fontSize: "0.95rem",
              maxWidth: "600px",
              mx: "auto",
            }}
          >
            Transparencia total, sin costos ocultos. Elige el plan que mejor se
            adapte a tu volumen de operación diaria.
          </Typography>
        </MotionBox>

        {/* Grid de Tarjetas: Carrusel AnimatePresence en Mobile / Grid en Desktop */}
        {isMobile ? (
          <Box sx={{ position: "relative", overflow: "visible", px: 1, pb: 2 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                onDragEnd={handleDragEnd}
                style={{
                  touchAction: "pan-y",
                  cursor: "grab",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ width: "100%", maxWidth: 380 }}>
                  <PlanCard plan={plans[activeIndex]} index={0} />
                </Box>
              </motion.div>
            </AnimatePresence>

            {/* Dots indicadores */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
                gap: 1.5,
              }}
            >
              {plans.map((p, i) => (
                <Box
                  key={p.name}
                  onClick={() => setActiveIndex(i)}
                  sx={{
                    width: i === activeIndex ? 28 : 8,
                    height: 8,
                    borderRadius: 4,
                    bgcolor:
                      i === activeIndex ? "#2C5FBE" : "rgba(44,95,190,0.15)",
                    cursor: "pointer",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      bgcolor:
                        i === activeIndex ? "#2C5FBE" : "rgba(44,95,190,0.3)",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: { xs: 2, md: 2 },
              alignItems: "stretch",
              px: { xs: 2, lg: 4 },
            }}
          >
            {plans.map((plan, i) => (
              <PlanCard key={plan.name} plan={plan} index={i} />
            ))}
          </Box>
        )}

        {/* Nota inferior */}
        <MotionBox
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.5 }}
          sx={{ textAlign: "center", mt: { xs: 1, md: 4 } }}
        >
          <Typography
            sx={{ fontSize: "0.8rem", color: "#94A3B8", fontWeight: 500 }}
          >
            ✓ Sin costo de implementación &nbsp;·&nbsp; ✓ Cancela cuando quieras
            &nbsp;·&nbsp; ✓ Soporte incluido
          </Typography>
        </MotionBox>
      </Container>
    </Box>
  );
}
